import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../data/gameConstants';
import { FullscreenSystem } from '../systems/FullscreenSystem';
import { OverlayInputLock } from '../systems/OverlayInputLock';
import { ResponsiveSystem } from '../systems/ResponsiveSystem';

const OVERLAY_ID = 'name-input-overlay';
const INPUT_ID = 'player-name-input';
const HINT_ID = 'name-input-default-hint';
const START_BTN_ID = 'name-input-start-btn';
const BACK_BTN_ID = 'name-input-back-btn';
const BODY_ACTIVE_CLASS = 'name-input-active';
const NAME_OVERLAY_HTML = `
        <div class="name-input-catcher" aria-hidden="true"></div>
        <div class="name-input-shell">
          <input
            id="${INPUT_ID}"
            class="name-input-field"
            type="text"
            inputmode="text"
            enterkeyhint="go"
            aria-label="Nama pemain"
            autocomplete="name"
            autocapitalize="words"
            spellcheck="false"
            maxlength="24"
            placeholder="Contoh: Bujang"
          />
          <p class="name-input-hint" id="${HINT_ID}"></p>
          <button type="button" class="name-input-btn primary" id="${START_BTN_ID}">Mulai Petualangan</button>
          <button type="button" class="name-input-btn" id="${BACK_BTN_ID}">Kembali</button>
        </div>
      `;

export interface NameInputOverlayConfig {
  placeholder?: string;
  maxLength?: number;
  onSubmit?: (name: string) => void;
  onChange?: (name: string) => void;
  onBack?: () => void;
}

export class NameInputOverlay {
  private overlay: HTMLDivElement;
  private shell: HTMLDivElement;
  private input: HTMLInputElement;
  private hint: HTMLParagraphElement;
  private startBtn: HTMLButtonElement;
  private backBtn: HTMLButtonElement;
  private onSubmit?: (name: string) => void;
  private onChange?: (name: string) => void;
  private onBack?: () => void;
  private lockedScene?: Phaser.Scene;
  private anchorX = GAME_WIDTH / 2;
  private anchorY = GAME_HEIGHT / 2;
  private anchorWidth = 420;
  private anchorAlign: 'top' | 'center' = 'top';
  private readonly inputHandler = () => {
    this.onChange?.(this.input.value);
  };
  private readonly keydownHandler = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.submit();
    }
  };
  private readonly pointerDownHandler = (e: Event) => {
    e.stopPropagation();
    this.focusInput();
  };
  private readonly startClickHandler = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    this.submit();
  };
  private readonly backClickHandler = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    this.onBack?.();
  };
  private readonly onLayoutChange = (): void => {
    window.requestAnimationFrame(() => this.positionOverlay());
  };

  constructor() {
    const mount = FullscreenSystem.getOverlayMount();
    let overlay = document.getElementById(OVERLAY_ID) as HTMLDivElement | null;
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = OVERLAY_ID;
      overlay.innerHTML = NAME_OVERLAY_HTML;
      mount.appendChild(overlay);
    } else if (overlay.parentElement !== mount) {
      mount.appendChild(overlay);
    }

    this.overlay = overlay;
    this.shell = overlay.querySelector('.name-input-shell') as HTMLDivElement;
    if (!this.shell) {
      overlay.innerHTML = NAME_OVERLAY_HTML;
      this.shell = overlay.querySelector('.name-input-shell') as HTMLDivElement;
    }
    this.input = overlay.querySelector(`#${INPUT_ID}`) as HTMLInputElement;
    this.hint = overlay.querySelector(`#${HINT_ID}`) as HTMLParagraphElement;
    this.startBtn = overlay.querySelector(`#${START_BTN_ID}`) as HTMLButtonElement;
    this.backBtn = overlay.querySelector(`#${BACK_BTN_ID}`) as HTMLButtonElement;

    this.input.addEventListener('input', this.inputHandler);
    this.input.addEventListener('keydown', this.keydownHandler);
    this.input.addEventListener('pointerdown', this.pointerDownHandler);
    this.input.addEventListener('touchstart', this.pointerDownHandler, { passive: true });
    this.startBtn.addEventListener('click', this.startClickHandler);
    this.backBtn.addEventListener('click', this.backClickHandler);
  }

  configure(config: NameInputOverlayConfig): void {
    this.onSubmit = config.onSubmit;
    this.onChange = config.onChange;
    this.onBack = config.onBack;
    if (config.placeholder) this.input.placeholder = config.placeholder;
    if (config.maxLength) this.input.maxLength = config.maxLength;
    this.clearError();
  }

  setGameAnchor(
    x: number,
    y: number,
    width: number,
    align: 'top' | 'center' = 'top',
  ): void {
    this.anchorX = x;
    this.anchorY = y;
    this.anchorWidth = width;
    this.anchorAlign = align;
    this.positionOverlay();
  }

  show(scene?: Phaser.Scene, value = ''): void {
    this.input.value = value;
    this.overlay.classList.add('visible');
    document.body.classList.add(BODY_ACTIVE_CLASS);
    this.positionOverlay();
    window.addEventListener('resize', this.onLayoutChange);
    window.addEventListener('orientationchange', this.onLayoutChange);
    document.addEventListener('fullscreenchange', this.onLayoutChange);
    document.addEventListener('webkitfullscreenchange', this.onLayoutChange);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.onLayoutChange);
      window.visualViewport.addEventListener('scroll', this.onLayoutChange);
    }
    if (scene) {
      this.lockedScene = scene;
      OverlayInputLock.acquire(scene);
    }
    if (!ResponsiveSystem.isMobileDevice()) {
      window.requestAnimationFrame(() => this.focusInput());
    }
  }

  hide(): void {
    this.overlay.classList.remove('visible');
    document.body.classList.remove(BODY_ACTIVE_CLASS);
    this.input.blur();
    window.removeEventListener('resize', this.onLayoutChange);
    window.removeEventListener('orientationchange', this.onLayoutChange);
    document.removeEventListener('fullscreenchange', this.onLayoutChange);
    document.removeEventListener('webkitfullscreenchange', this.onLayoutChange);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onLayoutChange);
      window.visualViewport.removeEventListener('scroll', this.onLayoutChange);
    }
    OverlayInputLock.release(this.lockedScene);
    this.lockedScene = undefined;
  }

  getValue(): string {
    return this.input.value.trim();
  }

  setValue(value: string): void {
    this.input.value = value;
  }

  focus(): void {
    this.focusInput();
  }

  private focusInput(): void {
    try {
      this.input.focus({ preventScroll: false });
    } catch {
      this.input.focus();
    }
    window.requestAnimationFrame(() => this.positionOverlay());
  }

  showError(message: string): void {
    this.hint.textContent = message;
    this.hint.hidden = false;
    this.hint.classList.add('error');
    this.input.classList.add('error');
  }

  clearError(): void {
    this.hint.textContent = '';
    this.hint.hidden = true;
    this.hint.classList.remove('error');
    this.input.classList.remove('error');
  }

  submit(): void {
    this.onSubmit?.(this.getValue());
  }

  destroy(): void {
    this.hide();
    this.input.removeEventListener('input', this.inputHandler);
    this.input.removeEventListener('keydown', this.keydownHandler);
    this.input.removeEventListener('pointerdown', this.pointerDownHandler);
    this.input.removeEventListener('touchstart', this.pointerDownHandler);
    this.startBtn.removeEventListener('click', this.startClickHandler);
    this.backBtn.removeEventListener('click', this.backClickHandler);
    this.onSubmit = undefined;
    this.onChange = undefined;
    this.onBack = undefined;
  }

  private positionOverlay = (): void => {
    const { left, top, scaleX } = ResponsiveSystem.gameCoordsToScreen(
      this.anchorX,
      this.anchorY,
    );

    const width = Math.max(160, this.anchorWidth * scaleX);
    const overlayHeight = this.shell.offsetHeight
      || this.overlay.offsetHeight
      || 180;
    let topPx = this.anchorAlign === 'top'
      ? top
      : top - overlayHeight / 2;

    const vv = window.visualViewport;
    if (vv && document.activeElement === this.input) {
      const maxTop = vv.offsetTop + vv.height - overlayHeight - 10;
      topPx = Math.min(topPx, maxTop);
      topPx = Math.max(vv.offsetTop + 8, topPx);
    }

    this.shell.style.transform = 'none';
    this.shell.style.left = `${left - width / 2}px`;
    this.shell.style.top = `${topPx}px`;
    this.shell.style.width = `${width}px`;
  };
}
